// src/pages/ConversationDetailsTestPage.tsx
import React, { useState, useEffect } from 'react';
import ConversationDetailsView from '@components/ConversationDetails/ConversationDetailsView';
import ConversationApi from '@components/ConversationDetails/api/ConversationDetailsApi';
import type { ApiConfig, AppConfig } from '@components/ConversationItem/types';
import type { ConversationDetail } from '@/types/conversation';
import { authStorage } from '../lib/storage';

// Helper function to get dynamic API configuration from session
const getApiConfig = (): ApiConfig & {
  refreshToken?: string;
  ExternalData?: string;
  SocketServer?: string;
  TenancyName: string;
  PersonId: string;
} =>
{
  const token = authStorage.getAccessToken() || '';
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost/Corgee.Web/';
  const socketServer =
    import.meta.env.VITE_SOCKET_SERVER || 'http://localhost:8880/usersNotificationManager';
  const tenancyName = import.meta.env.VITE_TENANCY_NAME || 'cgbot';

  // Try to get PersonId from storage
  let personId = '5205'; // Default
  try
  {
    const userData = sessionStorage.getItem('C2_data');
    if (userData)
    {
      const parsed = JSON.parse(userData);
      if (parsed.personid)
      {
        personId = String(parsed.personid);
      }
    }
  }
  catch
  {
    // Use default
  }

  return {
    url: apiUrl,
    token: token,
    refreshToken: '',
    ExternalData: '',
    SocketServer: socketServer,
    TenancyName: tenancyName,
    PersonId: personId,
  };
};

// Helper function to get dynamic App configuration
const getAppConfig = (): AppConfig & {
  buildVersion?: string;
  webChaturl?: string;
  appPrefix?: string;
} =>
{
  const webUrl = import.meta.env.VITE_WEB_URL || 'http://localhost/Corgee.WebClient/';
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost/Corgee.Web/';
  return {
    weburl: webUrl,
    apiurl: apiUrl,
    buildVersion: '1.0.0',
    webChaturl: webUrl + '/CHAT',
    appPrefix: '',
  };
};

// Storage Data Configuration - reads from real session/localStorage
const getStorageData = () =>
{
  let data: Record<string, any> = {};
  try
  {
    data = JSON.parse(sessionStorage.getItem('C2_data') || '{}');
  }
  catch
  {
    /* ignore */
  }

  let allLayers: any[] = [];
  try
  {
    allLayers = JSON.parse(sessionStorage.getItem('C2_AllLayers') || '[]');
  }
  catch
  {
    /* ignore */
  }

  let layersToHide: any[] = [];
  try
  {
    layersToHide = JSON.parse(sessionStorage.getItem('C2_LayersToHide') || '[]');
  }
  catch
  {
    /* ignore */
  }

  return {
    data,
    allLayers,
    layersToHide,
    layerId: parseInt(localStorage.getItem('C2_layerid') || '0'),
    conv_maxAllowedUpload: 20480000,
    enableChat: localStorage.getItem('C2_enableChat') === 'true',
    personid: parseInt(localStorage.getItem('C2_personid') || '0'),
  };
};

// Menu Configuration
const getConfigMenus = () => ({
  conversation: [
    'goto-project',
    'close-infowindow',
    'opendetails-newtab',
    'mark-redflag',
    'mark-important',
    'follow-conv',
    'copylink-to-clipboard',
    'create-itemtype',
    'submit-time',
  ],
  reply: ['mark-favorite', 'repost', 'edit', 'delete', 'create-itemtype'],
});

// Real Item Types Configuration
const realItemTypes = {
  Users: {
    Name: 'People',
    Icon: 'icons-menu-account-box',
  },
  Tasks: {
    Name: 'Tasks',
    Icon: 'icons-menu-epic',
  },
};

// User Data
const getUserData = () => ({
  imageProfile: 'Images/user.png?1.0.21.8',
  personName: 'Test User',
  PersonId: 0,
  UserName: 'Test User',
});

const ConversationDetailsTestPage: React.FC = () =>
{
  // Dynamic API and App configuration from session
  const [api] = useState(() => getApiConfig());
  const [config] = useState(() => getAppConfig());
  const [conversationId, setConversationId] = useState<number>(185343);
  const [inputId, setInputId] = useState<string>('185343');
  const [isInfoWindow, setIsInfoWindow] = useState<boolean>(false);
  const [isConversationDetailsView, setIsConversationDetailsView] = useState<boolean>(true);
  const [hasToken, setHasToken] = useState<boolean>(false);
  const [isSimulatingLoading, setIsSimulatingLoading] = useState<boolean>(false);

  // New state for pre-fetched conversation data
  const [conversationData, setConversationData] = useState<ConversationDetail | null>(null);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const isLoading = isFetching || isSimulatingLoading;

  // Check for token on mount
  useEffect(() =>
  {
    const token = authStorage.getAccessToken();
    setHasToken(!!token);
  }, []);

  // Fetch conversation data when conversationId changes
  useEffect(() =>
  {
    const fetchConversation = async () =>
    {
      if (!conversationId || !hasToken) return;

      setIsFetching(true);
      setFetchError(null);
      setConversationData(null);

      try
      {
        const response = await ConversationApi.getConversationById(api, {
          conversationId,
          layersToHide: '',
          getAllReplies: false,
          isConversationDetailsView: true,
          totalReplies: 0,
          isEnabled: true,
        });

        setConversationData(response);
      }
      catch (error)
      {
        console.error('Error fetching conversation:', error);
        setFetchError(error instanceof Error ? error.message : 'Unknown error');
      }
      finally
      {
        setIsFetching(false);
      }
    };

    fetchConversation();
  }, [conversationId, hasToken, api]);

  // Build the request object for ConversationDetailsView
  const buildRequest = () => ({
    api: api,
    enableLoading: false,
    showErrorMessage: true,
    isInfoWindow: isInfoWindow,
    isInfowindowViewCard: false,
    hasArchivedFilter: false,
    isConversationDetailsView: isConversationDetailsView,
    config: config,
    userData: getUserData(),
    storageData: getStorageData(),
    params: {
      ConversationId: conversationId,
      SocketConnectionId: undefined,
      IsFromProject: false,
      ConfigMenus: getConfigMenus(),
      ShowRefreshButton: true,
    },
    functions: {
      onLoadDataCallback: (_conversation: any) => {},
      onSendError: (_convId: number) => {},
      loadingBarStart: () => {},
      loadingBarComplete: () => {},
      openInfoWindow: (_conv: any) => {},
      attachReactFunctions: (_reactFunctions: any) => {},
      onUpdateConversation: (
        _convId: number,
        _newAttrs: any,
        _convObj: any,
        _renderType: any,
      ) => {},
      onAddReply: (_convId: number, _reply: any, _convObj: any) => {},
      onUpdateReply: (
        _convObj: any,
        _replyUpdated: any,
        _newAttrs: any,
        _newTotalFavorites: any,
      ) => {},
      addLayerToCard: (_e: any, _conversation: any, _layer: any, _callback: any) => {},
      getTaskUsers: (_e: any, _convId: number) => {},
      getTaskDashboards: (_e: any, _convId: number) => {},
      getTaskParent: (_e: any, _convId: number) => {},
      openFavoritesModal: (_e: any, _replyId: number) => {},
      goToProject: (_e: any, _convProject: any) => {},
      goToDashboard: () => {},
      editConversation: (_e: any, _currentConv: any) => {},
      deleteConversation: (_e: any, _currentConv: any) => {},
      onOpenGallery: (_e: any, _attachments: any) => {},
      onFileDownload: (_e: any, _attachment: any) => {},
      onCopyToClipboard: (_convId: number, _text: string, _result: any) => {},
      editReply: (_e: any, _currentReply: any, _currentConv: any) => {},
      deleteReply: (_e: any, _reply: any, _conversation: any) => {},
      archiveConversation: (_e: any, _conversation: any, callback: any) =>
      {
        if (callback) callback();
      },
      destroy: () => {},
      saveSearchResult: (_newDashboard: any) => {},
      copyTo: (_e: any, _conversation: any) => {},
      onReplyDeleted: (_e: any, _replyDeleted: any, _totalReplies: number) => {},
      onCreateItemFromCustomType: (_item: any, _conversation: any) => {},
      onItemConvertTo: (_conversation: any) => {},
      onChangedProject: (_e: any, _conversation: any) => {},
      onChangedSecurityGroups: (_e: any, _conversation: any) => {},
      onChangedGroupLayers: (_e: any, _conversation: any) => {},
      onChangeLayers: (_conversation: any, _isFromInfowindow?: boolean) => {},
      onCloseInfoWindow: () => {},
      getReloadItemFn: (_updateItemFn: any, _conversationid: number) => {},
      attachSocket: (_SocketConnectionId: string) => {},
      onDuplicateItem: (_e: any, _conversation: any) => {},
    },
    realItemTypes: realItemTypes,
    // Pass pre-fetched conversation data to skip internal API call
    conversation: conversationData || undefined,
  });

  // Handle form submission to load new conversation
  const handleLoadConversation = (e: React.FormEvent) =>
  {
    e.preventDefault();
    const newId = parseInt(inputId, 10);
    if (!isNaN(newId) && newId > 0)
    {
      setConversationId(newId);
    }
  };

  return (
    <div
      style={{
        padding: '20px',
        maxWidth: '1200px',
        margin: '0 auto',
        backgroundColor: '#f5f5f5',
        minHeight: '100vh',
      }}
    >
      <h1 style={{ marginBottom: '20px', color: '#333' }}>ConversationDetails Test Page</h1>

      {/* Token Status */}
      <div
        style={{
          padding: '10px 15px',
          marginBottom: '20px',
          borderRadius: '4px',
          backgroundColor: hasToken ? '#e8f5e9' : '#ffebee',
          color: hasToken ? '#2e7d32' : '#c62828',
        }}
      >
        {hasToken
          ? '✅ Token detected from session - using dynamic authentication'
          : '⚠️ No token found - please login first or check authentication'}
      </div>

      {/* Configuration Controls */}
      <section
        style={{
          marginBottom: '20px',
          padding: '20px',
          border: '1px solid #ddd',
          borderRadius: '8px',
          backgroundColor: 'white',
        }}
      >
        <h2 style={{ marginBottom: '15px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
          Configuration
        </h2>

        {/* Load Conversation Form */}
        <form onSubmit={handleLoadConversation} style={{ marginBottom: '15px' }}>
          <label style={{ marginRight: '10px', fontWeight: 'bold' }}>Conversation ID:</label>
          <input
            type="number"
            value={inputId}
            onChange={e => setInputId(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              marginRight: '10px',
              width: '120px',
            }}
          />
          <button
            type="submit"
            style={{
              padding: '8px 16px',
              backgroundColor: '#1877f2',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Load Conversation
          </button>
        </form>

        {/* View Options */}
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              checked={isInfoWindow}
              onChange={e => setIsInfoWindow(e.target.checked)}
            />
            <span>isInfoWindow</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              checked={isConversationDetailsView}
              onChange={e => setIsConversationDetailsView(e.target.checked)}
            />
            <span>isConversationDetailsView</span>
          </label>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: '#1877f2',
              fontWeight: 500,
            }}
          >
            <input
              type="checkbox"
              checked={isSimulatingLoading}
              onChange={e => setIsSimulatingLoading(e.target.checked)}
            />
            <span>Simulate Loading (Show Skeleton)</span>
          </label>
        </div>

        {/* API Info */}
        <div style={{ marginTop: '15px', fontSize: '12px', color: '#666' }}>
          <strong>API URL:</strong> {api.url}
          <br />
          <strong>Token:</strong> {api.token ? `${api.token.substring(0, 30)}...` : 'Not set'}
        </div>
      </section>

      {/* ConversationDetailsView Component */}
      <section
        style={{
          marginBottom: '20px',
          padding: '20px',
          border: '1px solid #ddd',
          borderRadius: '8px',
          backgroundColor: 'white',
        }}
      >
        <h2 style={{ marginBottom: '15px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
          Conversation #{conversationId}
        </h2>

        {/* Loading State - Skeleton */}
        {isLoading && (
          <div className="animate-pulse">
            {/* Header Skeleton */}
            <div
              style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '12px' }}
            >
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  backgroundColor: '#e0e0e0',
                  borderRadius: '50%',
                }}
              ></div>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    height: '16px',
                    backgroundColor: '#e0e0e0',
                    borderRadius: '4px',
                    marginBottom: '8px',
                    width: '30%',
                  }}
                ></div>
                <div
                  style={{
                    height: '12px',
                    backgroundColor: '#e0e0e0',
                    borderRadius: '4px',
                    width: '20%',
                  }}
                ></div>
              </div>
            </div>

            {/* Content Skeleton */}
            <div style={{ marginBottom: '15px' }}>
              <div
                style={{
                  height: '14px',
                  backgroundColor: '#e0e0e0',
                  borderRadius: '4px',
                  marginBottom: '8px',
                  width: '100%',
                }}
              ></div>
              <div
                style={{
                  height: '14px',
                  backgroundColor: '#e0e0e0',
                  borderRadius: '4px',
                  marginBottom: '8px',
                  width: '95%',
                }}
              ></div>
              <div
                style={{
                  height: '14px',
                  backgroundColor: '#e0e0e0',
                  borderRadius: '4px',
                  marginBottom: '8px',
                  width: '90%',
                }}
              ></div>
              <div
                style={{
                  height: '14px',
                  backgroundColor: '#e0e0e0',
                  borderRadius: '4px',
                  width: '85%',
                }}
              ></div>
            </div>

            {/* Actions Skeleton */}
            <div style={{ display: 'flex', gap: '15px', marginTop: '20px' }}>
              <div
                style={{
                  height: '32px',
                  backgroundColor: '#e0e0e0',
                  borderRadius: '4px',
                  width: '80px',
                }}
              ></div>
              <div
                style={{
                  height: '32px',
                  backgroundColor: '#e0e0e0',
                  borderRadius: '4px',
                  width: '80px',
                }}
              ></div>
              <div
                style={{
                  height: '32px',
                  backgroundColor: '#e0e0e0',
                  borderRadius: '4px',
                  width: '80px',
                }}
              ></div>
            </div>

            {/* Replies Skeleton */}
            <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #e0e0e0' }}>
              <div
                style={{
                  height: '18px',
                  backgroundColor: '#e0e0e0',
                  borderRadius: '4px',
                  marginBottom: '15px',
                  width: '25%',
                }}
              ></div>

              {[1, 2].map(i => (
                <div key={i} style={{ marginBottom: '20px', paddingLeft: '20px' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '10px',
                    }}
                  >
                    <div
                      style={{
                        width: '32px',
                        height: '32px',
                        backgroundColor: '#e0e0e0',
                        borderRadius: '50%',
                      }}
                    ></div>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          height: '14px',
                          backgroundColor: '#e0e0e0',
                          borderRadius: '4px',
                          width: '25%',
                        }}
                      ></div>
                    </div>
                  </div>
                  <div
                    style={{
                      height: '12px',
                      backgroundColor: '#e0e0e0',
                      borderRadius: '4px',
                      marginBottom: '6px',
                      width: '90%',
                    }}
                  ></div>
                  <div
                    style={{
                      height: '12px',
                      backgroundColor: '#e0e0e0',
                      borderRadius: '4px',
                      width: '70%',
                    }}
                  ></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Error State */}
        {fetchError && !isLoading && (
          <div
            style={{
              padding: '20px',
              backgroundColor: '#ffebee',
              color: '#c62828',
              borderRadius: '4px',
              marginBottom: '15px',
            }}
          >
            Error loading conversation: {fetchError}
          </div>
        )}

        {/* Conversation View - only render when data is loaded */}
        {conversationData && !isLoading && <ConversationDetailsView request={buildRequest()} />}
      </section>

      {/* Debug Info */}
      <details style={{ marginTop: '20px' }}>
        <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '10px' }}>
          Debug: Request Configuration
        </summary>
        <pre
          style={{
            backgroundColor: '#f8f9fa',
            padding: '15px',
            borderRadius: '4px',
            overflow: 'auto',
            maxHeight: '400px',
            fontSize: '12px',
          }}
        >
          {JSON.stringify(buildRequest(), null, 2)}
        </pre>
      </details>
    </div>
  );
};

export default ConversationDetailsTestPage;
